/**
 * Terms, privacy and the "what we collect" disclosure.
 *
 * The telemetry section is generated from `GET /v1/telemetry/catalog` — the same
 * catalog the backend validator enforces. That is the point: a privacy page written
 * by hand drifts from the code within a quarter, and then it is a false statement
 * rather than a policy.
 *
 * NOT LEGAL ADVICE. These are drafting placeholders with the technical facts filled
 * in accurately. Counsel must review before launch (blueprint §2.3).
 */

import { Link } from 'react-router-dom'

import { env } from '@/app/env'
import { useTelemetryCatalog } from '@/shared/api/hooks'
import { PublicPage } from '@/shared/ui/Chrome'
import { Badge, Label, Notice, Panel } from '@/shared/ui/primitives'

/**
 * What the telemetry endpoint can never receive, as the API publishes it
 * (archis-api handlers_commerce.go, `never_collected`). Rendered straight away so the
 * list is on the page when the API is slow or down; the live copy replaces it.
 * `npm run check:never-collected` fails the build if the two ever differ.
 */
const NEVER_COLLECTED = [
  'source code',
  'file paths',
  'directory names',
  'repository names or URLs',
  'symbol, function, class or variable names',
  'queries or prompts',
  'patches or diffs',
  'graph contents',
  'test output',
  'error messages',
  'environment variables',
  'hostnames',
  'usernames',
  'IP addresses',
]

function LegalShell({ title, index, children }: { title: string; index: string; children: React.ReactNode }) {
  return (
    <PublicPage>
      <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s9)' }}>
        <Label index={index}>{title}</Label>
        <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', marginTop: 'var(--s3)' }}>{title}</h1>
        <div className="prose" style={{ marginTop: 'var(--s6)' }}>
          {children}
        </div>
      </section>
    </PublicPage>
  )
}

export function Terms() {
  return (
    <LegalShell title="Terms & EULA" index="90">
      <Notice tone="warn">
        Draft. This document must be reviewed by counsel before launch. The technical
        statements are accurate; the legal framing is a placeholder.
      </Notice>

      <h2>1. What you are buying</h2>
      <p>
        A licence to use the Archis compiler, runtime, language frontends and agent
        integrations included in your plan, on the number of devices your plan permits,
        for as long as your subscription is current. You are not buying the source code,
        and no ownership transfers.
      </p>

      <h2>2. What you receive</h2>
      <p>
        Signed platform binaries, checksums and signatures, a release manifest, an SBOM,
        installer scripts, product updates on your channel, an account in this portal, and
        support at the level your plan includes.
      </p>

      <h2>3. Permitted use</h2>
      <ul>
        <li>Commercial use by the licensed individual or by seat holders (Team).</li>
        <li>Unlimited local repositories. No file, project or context limits.</li>
        <li>Installation on up to the number of active devices your plan permits.</li>
      </ul>

      <h2>4. Prohibited use</h2>
      <ul>
        <li>Redistributing, sublicensing, renting or reselling the binaries.</li>
        <li>Sharing a licence or an account between people.</li>
        <li>
          Circumventing licence validation, device limits or entitlement checks, including
          patching the binary or forging an entitlement lease.
        </li>
        <li>Reverse engineering except where that right cannot lawfully be excluded.</li>
      </ul>

      <h2>5. The Free plan</h2>
      <p>
        Archis Free is available to any account, indefinitely, with no payment method. It
        covers the local engine — indexing, queries and agent integration — on up to two
        active devices, for repositories you own personally. Repositories owned by an
        organization, shared workspaces, committed repository defaults and audit logs
        require a paid plan.
      </p>
      <p>
        If a paid subscription ends, the account returns to Archis Free rather than losing
        access: protected commands keep working within what Free covers, and your local
        index, graph and sessions are never deleted.
      </p>

      <h2>6. Billing and cancellation</h2>
      <p>
        Subscriptions are billed in advance by Stripe, monthly or annually. Team
        subscriptions are billed per purchased seat. Changing the seat count mid-cycle is
        prorated: added seats are charged at once, and unused time on removed seats is
        credited toward your next invoice. You may cancel at any time from the billing
        portal and retain access until the end of the period you have paid for.
      </p>

      <h2>7. Licence validation</h2>
      <p>
        Archis verifies a signed entitlement locally on every protected command, refreshes it
        in the background after 12 hours, requires a successful online validation every 24
        hours, and then permits a further 72 hours of offline grace. When validation is not
        possible beyond that window, protected commands are blocked. Local data is never
        deleted as a consequence of a licensing failure.
      </p>

      <h2>8. Your code</h2>
      <p>
        Archis performs all indexing, retrieval and context selection on your machine. We do
        not receive your source code, file paths, symbol names, queries, prompts, patches,
        graph contents or test output. See the <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>9. Warranty and liability</h2>
      <p>
        The software is provided as is, without warranties beyond those that cannot lawfully
        be excluded. Our aggregate liability is limited to the fees you paid in the twelve
        months preceding the claim. Placeholder — counsel to finalise.
      </p>

      <h2>10. Termination</h2>
      <p>
        This licence terminates if you materially breach it, and on non-payment after the
        documented grace period. On termination you must stop using the software and remove
        the binaries; your local project data remains yours.
      </p>

      <h2>11. Open-source components</h2>
      <p>
        Archis includes third-party open-source components. Their licences and attributions
        ship with each release and are listed in the SBOM.
      </p>

      <h2 id="trademarks">12. Trademarks</h2>
      <p>
        Claude Code, Cursor, Codex, Gemini CLI, Antigravity, Okta, Microsoft Entra ID and the
        other product names on this site are trademarks of their respective owners. We name
        them to say what Archis works with; Archis is not affiliated with or endorsed by them.
      </p>

      <p className="mono-note">
        Questions: <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>
      </p>
    </LegalShell>
  )
}

export function Privacy() {
  const { data: catalog } = useTelemetryCatalog()

  return (
    <LegalShell title="Privacy" index="91">
      <Notice tone="warn">
        Draft. Counsel must review before launch. The technical statements below are
        generated from the running system and are accurate.
      </Notice>

      <h2>The short version</h2>
      <p>
        <strong>Your source code never reaches us.</strong> Archis compiles, indexes, retrieves
        and selects context entirely on your machine. Our servers handle your account,
        billing, licence checks, workspace administration, support requests and release
        metadata, plus CLI analytics only if you turn them on. Everything they hold is
        listed below.
      </p>

      <h2>What we hold</h2>
      <ul>
        <li>
          <strong>Account:</strong> your email address, display name, avatar URL and which
          providers you have signed in with, from Google or GitHub via Firebase
          Authentication.
        </li>
        <li>
          <strong>Billing:</strong> a Stripe customer reference, your plan, subscription
          status, period dates and seat count. Card details are held by Stripe and never
          reach our servers.
        </li>
        <li>
          <strong>Devices:</strong> a one-way hash of a random installation identifier your
          CLI generates, plus platform, architecture, CLI version and last-seen time. We do
          not collect hardware serial numbers, MAC addresses or machine names.
        </li>
        <li>
          <strong>Licence checks:</strong> each time the CLI renews its licence we record which
          device, account and workspace it was issued to, the plan and when. These records
          are deleted automatically about 34 days after issue.
        </li>
        <li>
          <strong>Organizations:</strong> workspace name, members, roles, seats, policies and
          an append-only audit log of administrative actions. Where a Team workspace is
          billed by active developers, the first and last date in each billing period on
          which each member used the workspace's licence, kept for 400 days so an invoice
          can be explained.
        </li>
        <li>
          <strong>Support:</strong> the requests you open, the replies, and any screenshots or
          reports you choose to attach.
        </li>
        <li>
          <strong>Operational logs:</strong> request metadata with a hashed IP address.
          Access tokens, refresh tokens, ID tokens, activation codes and payment data are
          never logged.
        </li>
      </ul>

      <h2 id="telemetry">CLI product analytics</h2>
      <p>
        Telemetry is <strong>off until you turn it on</strong>. Check with{' '}
        <code>chi telemetry status</code>, turn it on with <code>chi telemetry on</code>,
        off again with <code>chi telemetry off</code>, and inspect exactly what would be
        sent with <code>chi telemetry preview</code> — that command prints the pending batch
        and sends nothing.
      </p>
      <p>
        In continuous-integration environments nothing is collected and you are never
        prompted, regardless of the setting. Team administrators control the mode for their
        whole organization, and <code>off</code> is the default.
      </p>

      <h3>Never collected — in any mode</h3>
      <p>
        There is no setting that enables any of the following, because the server rejects it
        and the endpoint to receive it does not exist:
      </p>
      {/* The built-in list renders at once and survives an API outage; the live one
          replaces it when it loads, and check:never-collected keeps the two identical. */}
      <div className="row" style={{ marginBottom: 'var(--s5)' }}>
        {(catalog?.never_collected ?? NEVER_COLLECTED).map((item) => (
          <Badge key={item} tone="coral">
            {item}
          </Badge>
        ))}
      </div>

      <h3>Collected when you opt in</h3>
      <p>
        Every event below is fixed by an allowlist enforced on our servers. An event that is
        not on this list is rejected. A property that is not declared for its event is
        rejected. Numeric values are placed in coarse buckets before storage — we store{' '}
        <code>1k-10k</code>, never <code>4,271</code>. Adding a metric requires a server
        deploy, which is what makes this list reviewable in a single diff.
      </p>

      {catalog && (
        <div className="stack" style={{ ['--gap' as string]: 'var(--s4)', marginBottom: 'var(--s5)' }}>
          {catalog.events.map((event) => (
            <Panel key={event.name} title={event.name} index="ev">
              <p style={{ marginTop: 0 }}>{event.description}</p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Type</th>
                      <th>Accepted values</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.properties.map((property) => (
                      <tr key={property.name}>
                        <td className="fig">{property.name}</td>
                        <td className="mono-note">{property.kind}</td>
                        <td className="mono-note">
                          {property.allowed && property.allowed.length > 0
                            ? property.allowed.join(' · ')
                            : property.kind === 'bucket'
                              ? 'coarse bucket label'
                              : property.kind === 'bool'
                                ? 'true / false'
                                : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <h3>Identifiers in telemetry</h3>
      <p>
        Batches carry a peppered hash of a random installation identifier — not your account,
        not your IP address. Events are linked to an account only when you are signed in{' '}
        <em>and</em> your organization's telemetry mode is <code>full</code>. In{' '}
        <code>aggregate</code> mode nothing is traceable to a person. Timestamps are
        truncated to the hour by the client, because minute-level timing on a single
        developer's machine is a behavioural fingerprint and buys no analytical value.
      </p>
      <p>Analytics events are deleted automatically after 400 days by a database-enforced expiry.</p>

      <h2 id="portal-analytics">Website and account-portal analytics</h2>
      <p>
        The public site and signed-in portal send product analytics to PostHog. That stream
        is separate from CLI
        telemetry: it covers funnel events such as sign-in, checkout, downloads and
        documentation navigation. Events are linked to your Firebase user id after you sign
        in. Source code, repository contents, prompts, tokens and secrets are never included.
        Session recording is disabled.
      </p>

      <h2>Diagnostics</h2>
      <p>
        <code>chi doctor</code> prints what it found and sends nothing;{' '}
        <code>chi doctor --copy</code> also puts that report on your clipboard, to paste into
        a support request. A crash prints a link that opens a support request in the portal
        with the version, the platform and where in Archis it died already filled in — you
        decide whether to submit it. Both <em>can</em> contain repository paths, so read them before
        sharing. Nothing is uploaded unless you choose to attach it to a support request, and
        Team administrators can forbid uploads entirely.
      </p>

      <h2>Subprocessors</h2>
      <ul>
        <li>Google Firebase Authentication — sign-in</li>
        <li>Coolify — application hosting</li>
        <li>Cloudflare — DNS, and R2 for the CLI artifacts you download</li>
        <li>MongoDB Atlas — account, licensing and analytics storage</li>
        <li>Stripe — payments and invoicing</li>
        <li>PostHog — website and account-portal product analytics</li>
        <li>A transactional email provider — invitations and account notices</li>
      </ul>
      <p className="mono-note">
        Final list, regions and the DPA are pending counsel review before launch.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>Disable telemetry at any time with <code>chi telemetry off</code>.</li>
        <li>
          Delete the local analytics queue with <code>chi telemetry purge</code>.
        </li>
        <li>Revoke any device from the portal or with <code>chi keys devices --revoke &lt;id&gt;</code>.</li>
        <li>
          Request export or deletion of your account data at{' '}
          <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>.
        </li>
      </ul>
    </LegalShell>
  )
}
